import { ref } from "vue";
import { DEFAULT_THEME, themeOptions } from "./themes";

const THEME_STORAGE_KEY = "lemmon-theme";

export type StatusBarStyle = "light" | "dark" | "auto";

const currentTheme = ref<string>(DEFAULT_THEME);

// The native (Expo) layer owns the status bar, but useTheme must not depend on
// the app-level bridge. It instead dispatches the desired status bar style
// through a callback the app registers via setStatusBarDispatcher once the
// native RPC is ready. Any style requested before registration is flushed as
// soon as the dispatcher is set (covers the initial load).
let statusBarDispatcher: ((style: StatusBarStyle) => void) | null = null;
let pendingStatusBarStyle: StatusBarStyle | null = null;

const isDarkTheme = (themeId: string): boolean => {
  const option = themeOptions.find((t) => t.id === themeId);
  const fallback = themeOptions.find((t) => t.id === DEFAULT_THEME);
  return option?.isDark ?? fallback?.isDark ?? true;
};

// A dark theme has a dark surface, so the status bar needs light-colored
// content (and vice versa).
const statusBarStyleForTheme = (themeId: string): StatusBarStyle =>
  isDarkTheme(themeId) ? "light" : "dark";

const syncStatusBar = (themeId: string) => {
  const style = statusBarStyleForTheme(themeId);
  if (statusBarDispatcher) {
    statusBarDispatcher(style);
  } else {
    pendingStatusBarStyle = style;
  }
};

// Called by the app (NativeBridgeProvider) with a function that forwards the
// style to the native changeStatusBar RPC.
export const setStatusBarDispatcher = (
  dispatch: (style: StatusBarStyle) => void,
) => {
  statusBarDispatcher = dispatch;
  if (pendingStatusBarStyle) {
    dispatch(pendingStatusBarStyle);
    pendingStatusBarStyle = null;
  }
};

const applyTheme = (themeId: string) => {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", themeId);
  }
  syncStatusBar(themeId);
};

const getStoredTheme = (): string => {
  if (typeof localStorage === "undefined") return DEFAULT_THEME;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && themeOptions.some((t) => t.id === stored)) {
    return stored;
  }
  return DEFAULT_THEME;
};

// Apply the stored theme immediately so the app doesn't flash the default theme on load.
currentTheme.value = getStoredTheme();
applyTheme(currentTheme.value);

export const useTheme = () => {
  const setTheme = (themeId: string) => {
    currentTheme.value = themeId;
    applyTheme(themeId);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    }
  };

  return {
    currentTheme,
    themeOptions,
    setTheme,
  };
};
